import * as Graphics from "../graphics/graphics.js"
import { showSmallLengthWithUnits, showBigPointLoad, bigPointLoadUnit, showLengthWithUnits, showBigMoment, bigMomentUnit,
  showSmallPointLoad, showSmallMoment } from 'ws-units'

import { steel_span_attr, wood_span_attr, custom_shape_span_attr } from "../graphics/attributes.js"

import Raphael from 'raphael'

const bendingResultViewPaper = Raphael(
  "bending-results-view",
  document.getElementById("bending-results-view").offsetWidth,
  document.getElementById("bending-results-view").offsetHeight)

const shearResultViewPaper = Raphael(
  "shear-results-view",
  document.getElementById("shear-results-view").offsetWidth,
  document.getElementById("shear-results-view").offsetHeight)

const deflectionResultViewPaper = Raphael(
  "deflection-results-view",
  document.getElementById("deflection-results-view").offsetWidth,
  document.getElementById("deflection-results-view").offsetHeight)


const RESULT_CHECK_TYPE = {
  BENDING: "bending_checks",
  SHEAR: "shear_checks",
  DEFLECTION: "deflection_checks"
}

const CANVAS_HEIGHT = 280 // height of the canvas in pixels
const BEAM_T = 25 // thickness of the beam in pixels
const Y_OFFSET = 100 // offset for the beam diagram in the results view
const BEAM_LENGTH_SCALE = 0.9 // scale for the beam length in the results view

// this is what our python api returns - use this to get the correct service case equation index
const SERVICE_CASES = ["L", "L + Lr", "L + S", "D + L", "Snow", "Wind", "Total"]


function getControllingResultSet(project, results, resultCheckType) {
  // get the controlling load case or load combination
  let designType = project.design.designType
  let controllingCaseID = results[resultCheckType].controlling_case
  let controllingEquationIndex;
  if(resultCheckType === RESULT_CHECK_TYPE.DEFLECTION) {
    // special case for servcies cases: get the index that matches the controllingCaseID in the SERVICES_CASES array
    controllingEquationIndex = SERVICE_CASES.findIndex(eq => eq === controllingCaseID)
  } else {
    controllingEquationIndex = Number(controllingCaseID.split('EQ')[1]) - 1
  }

  let controllingResultSet;

  // get the result set
  switch (designType) {
    case "ASD":
    case "LRFD":
      if(resultCheckType === RESULT_CHECK_TYPE.DEFLECTION) {
        controllingResultSet = results.service_case_results[controllingEquationIndex];
      } else {
        controllingResultSet = results.combination_results.find(cc => cc.equation_name === controllingCaseID);
      }
      break;
    case 'None':
      if(resultCheckType === RESULT_CHECK_TYPE.DEFLECTION) {
        controllingResultSet = results.service_case_results[controllingEquationIndex];
      } else {
        controllingResultSet = results.load_case_results.find(cc => cc.equation_name === controllingCaseID);
      }
      break;
  }
  
  return controllingResultSet
}

export function drawResultsView(project, results, forPrint) {
  if (results == null || Object.keys(results).length === 0) { return }

  if (project.beam.nodes.length <= 1) { return }

  document.getElementById("bending-results-view-card").classList.remove('d-none')
  document.getElementById("shear-results-view-card").classList.remove('d-none')
  document.getElementById("deflection-results-view-card").classList.remove('d-none')

  drawResultsViewForBending(project, results, forPrint)
  drawResultsViewForShear(project, results, forPrint)
  drawResultsViewForDeflection(project, results, forPrint)

}


function configureBendingResultCardAppearance(project, results) {
  const design = project.design;
  var checks = results["bending_checks"];
  var controlling_unity = checks["controlling_unity"];
  var controlling_case = checks["controlling_case"];
  var controlling_equation = checks["controlling_code_equation"];
  var controlling_case = checks["controlling_case"];
  
  const nice_eq_index = Number(controlling_case.split('EQ')[1])

  var nice_case = design.loadCombination["prettyEquations"][nice_eq_index-1]["EQ"];
  var success = controlling_unity < 1.0;
  var designStatusStr = success ? '<i class="fa fa-check"></i>' : '<i class="fa fa-remove"></i>';
  
  if(success) {
    document.getElementById('bending-results-view-heading').classList.add('ws-design-succeeded')
    document.getElementById('bending-results-view-heading').classList.remove('ws-design-failed')
  } else {
    document.getElementById('bending-results-view-heading').classList.remove('ws-design-succeeded')
    document.getElementById('bending-results-view-heading').classList.add('ws-design-failed')
  }

  let str = `
    <div class="fs-6">${designStatusStr} Bending ${controlling_unity.toFixed(2)} 
      <span class="float-end">${controlling_equation} (${nice_case})</span>
    </div>`

    document.getElementById('bending-results-view-heading').innerHTML = str

}

function drawResultsViewForBending(project, results, forPrint) {

  let controllingResultSet = getControllingResultSet(project, results, RESULT_CHECK_TYPE.BENDING)

  if (controllingResultSet == null || controllingResultSet === undefined) {
    console.log("No controlling result set found");
    return;
  }

  configureBendingResultCardAppearance(project, results)
  
  let paper = bendingResultViewPaper

  paper.clear()

  const canvasW = forPrint === true ? 800 : Math.min(document.getElementById("bending-results-view").offsetWidth, 900)
  const canvasH = CANVAS_HEIGHT

  paper.setSize(canvasW, canvasH)

  const totalLength = project.beam.TotalLength()
  const xScale = BEAM_LENGTH_SCALE * canvasW / totalLength
  const xStart = 0.05 * totalLength * xScale

  const xs = controllingResultSet["displacements"].map(d => xStart + xScale * d["x"]);
  const values = controllingResultSet.nodal_forces.map(f => f['mz'])
  
  // get max and min values
  let valueMin = 1e9
  let valueMax = -1e9
  // get min and max values 
  values.forEach(val => {  
    valueMin = Math.min(valueMin, val)
    valueMax = Math.max(valueMax, val)
  })

  // now we need to swap the min and max values if the min is greater than the max
  if (Math.abs(valueMin) < Math.abs(valueMax)) { 
    const v = valueMin
    valueMin = valueMax
    valueMax = v
  }

  // now we want to scale the values so that the maximum is 1
  const maxValue = Math.max(Math.abs(valueMin), Math.abs(valueMax));
  const normalizedValues = values.map(value => {
    return value / maxValue;
  });

  drawBeamDiagramResultsView(project, xs, normalizedValues, paper, canvasW) 

  const maxMz = controllingResultSet.max_mz
  
  Graphics.drawExtremeDimension(xStart, xStart + maxMz.x * xScale, Y_OFFSET + 100,
    `Max Moment = ${showBigMoment(maxMz.value)} ${bigMomentUnit()} @ ${showLengthWithUnits(maxMz.x)}`, paper)

  drawReactions(project, controllingResultSet, xStart, Y_OFFSET, xScale, paper) 
  
}

function configureShearResultCardAppearance(project, results) {
  const design = project.design;
  var checks = results["shear_checks"];
  var controlling_unity = checks["controlling_unity"];
  var controlling_case = checks["controlling_case"];
  var controlling_equation = checks["controlling_code_equation"];
  var controlling_case = checks["controlling_case"];
  
  const nice_eq_index = Number(controlling_case.split('EQ')[1])

  var nice_case = design.loadCombination["prettyEquations"][nice_eq_index-1]["EQ"];
  var success = controlling_unity < 1.0;
  var designStatusStr = success ? '<i class="fa fa-check"></i>' : '<i class="fa fa-remove"></i>';
  
  if(success) {
    document.getElementById('shear-results-view-heading').classList.add('ws-design-succeeded')
    document.getElementById('shear-results-view-heading').classList.remove('ws-design-failed')
  } else {
    document.getElementById('shear-results-view-heading').classList.remove('ws-design-succeeded')
    document.getElementById('shear-results-view-heading').classList.add('ws-design-failed')
  }

  let str = `
    <div class="fs-6">${designStatusStr} Shear ${controlling_unity.toFixed(2)} 
      <span class="float-end">${controlling_equation} (${nice_case})</span>
    </div>`

    document.getElementById('shear-results-view-heading').innerHTML = str

}

function drawResultsViewForShear(project, results, forPrint) {

  let controllingResultSet = getControllingResultSet(project, results, RESULT_CHECK_TYPE.SHEAR)

  if (controllingResultSet == null || controllingResultSet === undefined) {
    console.log("No controlling result set found");
    return;
  }

  configureShearResultCardAppearance(project, results) 

  let paper = shearResultViewPaper

  paper.clear()

  const canvasW = forPrint === true ? 800 : Math.min(document.getElementById("shear-results-view").offsetWidth, 900)
  const canvasH = CANVAS_HEIGHT

  paper.setSize(canvasW, canvasH)

  const totalLength = project.beam.TotalLength()
  const xScale = BEAM_LENGTH_SCALE * canvasW / totalLength
  const xStart = 0.05 * totalLength * xScale

  const xs = controllingResultSet["displacements"].map(d => xStart + xScale * d["x"]);
  const values = controllingResultSet.nodal_forces.map(f => f['fy'])
  
  // get max and min values
  let valueMin = 1e9
  let valueMax = -1e9
  // get min and max values 
  values.forEach(val => {  
    valueMin = Math.min(valueMin, val)
    valueMax = Math.max(valueMax, val)
  })

  // swap the min and max values if the min is greater than the max
  if (Math.abs(valueMin) < Math.abs(valueMax)) { 
    const v = valueMin
    valueMin = valueMax
    valueMax = v
  }

  const maxValue = Math.max(Math.abs(valueMin), Math.abs(valueMax));
  const normalizedValues = values.map(value => {
    return value / maxValue;
  });

  drawBeamDiagramResultsView(project, xs, normalizedValues, paper, canvasW) 

  const maxFy = controllingResultSet.max_fy
  
  Graphics.drawExtremeDimension(xStart, xStart + maxFy.x * xScale, Y_OFFSET + 100,
    `Max Shear = ${showBigPointLoad(maxFy.value)} ${bigPointLoadUnit()} @ ${showLengthWithUnits(maxFy.x)}`, paper)
  
  drawReactions(project, controllingResultSet, xStart, Y_OFFSET, xScale, paper) 
  
}

function configureDeflectionResultCardAppearance(project, results) {

  var checks = results["deflection_checks"];
  var controlling_unity = checks["controlling_unity"];
  var controlling_case = checks["controlling_case"];
  var success = controlling_unity < 1.0;
  var designStatusStr = success ? '<i class="fa fa-check"></i>' : '<i class="fa fa-remove"></i>';

  var success = controlling_unity < 1.0;
  var designStatusStr = success ? '<i class="fa fa-check"></i>' : '<i class="fa fa-remove"></i>';
  
  if(success) {
    document.getElementById('deflection-results-view-heading').classList.add('ws-design-succeeded')
    document.getElementById('deflection-results-view-heading').classList.remove('ws-design-failed')
  } else {
    document.getElementById('deflection-results-view-heading').classList.remove('ws-design-succeeded')
    document.getElementById('deflection-results-view-heading').classList.add('ws-design-failed')
  }

  let str = `
    <div class="fs-6">${designStatusStr} Deflection ${controlling_unity.toFixed(2)} 
      <span class="float-end">Service Case (${controlling_case})</span>
    </div>`

    document.getElementById('deflection-results-view-heading').innerHTML = str

}

function drawResultsViewForDeflection(project, results, forPrint) {

  let controllingResultSet = getControllingResultSet(project, results, RESULT_CHECK_TYPE.DEFLECTION)

  if (controllingResultSet == null || controllingResultSet === undefined) {
    console.log("No controlling result set found");
    return;
  }

  configureDeflectionResultCardAppearance(project, results)

  let paper = deflectionResultViewPaper

  paper.clear()

  const canvasW = forPrint === true ? 800 : Math.min(document.getElementById("deflection-results-view").offsetWidth, 900)
  const canvasH = CANVAS_HEIGHT

  paper.setSize(canvasW, canvasH)

  const totalLength = project.beam.TotalLength()
  const xScale = BEAM_LENGTH_SCALE * canvasW / totalLength
  const xStart = 0.05 * totalLength * xScale

  const xs = controllingResultSet["displacements"].map(d => xStart + xScale * d["x"]);
  const values = controllingResultSet["displacements"].map(d => d['dy'])
  
  // get max and min values
  let valueMin = 1.0e9
  let valueMax = -1.0e9
  // get min and max values 
  values.forEach(val => {  
    valueMin = Math.min(valueMin, val)
    valueMax = Math.max(valueMax, val)
  })

  // now we need to swap the min and max values if the min is greater than the max
  if (Math.abs(valueMin) < Math.abs(valueMax)) { 
    const v = valueMin
    valueMin = valueMax
    valueMax = v
  }

  // now we want to scale the values so that the maximum value is 5 times the height of the beam
  const maxValue = Math.max(Math.abs(valueMin), Math.abs(valueMax));
  const normalizedValues = values.map(value => {
    return value / maxValue;
  });

  drawBeamDiagramResultsView(project, xs, normalizedValues, paper, canvasW) 

  const maxDy = controllingResultSet.max_dy
  
  Graphics.drawExtremeDimension(xStart, xStart + maxDy.x * xScale, Y_OFFSET + 100,
    `Max Dy = ${showSmallLengthWithUnits(maxDy.value)} @ ${showLengthWithUnits(maxDy.x)}`, paper)
  
  drawReactions(project, controllingResultSet, xStart, Y_OFFSET, xScale, paper, true) 
  
}

function drawReactions(project, controllingResultSet, xStart, Y_OFFSET, xScale, paper, isService = false) {
  var nReactions = controllingResultSet["node_reactions"].length

  let factoredLoadCases = project.design.designType === 'None' ? project.design.loadCases : project.design.loadCombination;
  
  if(isService) {
    factoredLoadCases = project.design.serviceCases
  }

  // now get the index from the equation_name 
  const index = Number(controllingResultSet["equation_name"].split('EQ')[1]) - 1;
  const equation = factoredLoadCases["equations"][index];
  const loadCaseLabels = Object.keys(equation)
  

  for (var s = 0; s < nReactions; s++) {
    var x = controllingResultSet["node_reactions"][s]["x"];
    if (project.beam.isNodeAtThisLocation(x) === false) {
      continue
    }

    // we also check to see if there is a point load directly over a support. the finite element method
    // will not show a reaction at this location so we manually add it in.
    // if there is a point load here and the support is fixed or pinned we apply our combination equation:
    //    sum(equation[loadCase] * pointLoadAtReaction[loadCase])
    var force = controllingResultSet["node_reactions"][s]["fy"];
    var pointLoadAtReaction = project.beam.getPointLoadAtLocation(x);
    if (pointLoadAtReaction && project.beam.isSupportPinnedOrFixed(x)) {
      var additionalLoad = 0;
      loadCaseLabels.forEach(loadCaseLabel => {
        const factor = Number(equation[loadCaseLabel]);
        const load = Number(pointLoadAtReaction[loadCaseLabel]);
        if (!isNaN(factor) && !isNaN(load)) {
          additionalLoad += factor * load;
        }
      });
      force += additionalLoad;
    }

    if (force !== 0) {
      force = Number(showSmallPointLoad(force)).toFixed(0)
      var forceLabel = showBigPointLoad(force) + " " + bigPointLoadUnit();
      var direction = force > 0 ? "up" : "down";
      Graphics.drawForceReaction(direction, xStart + xScale * x, Y_OFFSET, forceLabel, paper);
    }

    var moment = controllingResultSet["node_reactions"][s]["mz"];
    if (moment !== 0) {
      moment = Number(showSmallMoment(moment)).toFixed(0)
      const momentLabel = showBigMoment(moment) + bigMomentUnit();
      Graphics.drawMomentReaction(xStart + xScale * x, Y_OFFSET, 0, momentLabel, paper);
    }
  }

}


function drawBeamDiagramResultsView(project, xs, gradientValues, paper, canvasW) {

  const beam = project.beam;
  
  const totalLength = project.beam.TotalLength()
  const xScale = BEAM_LENGTH_SCALE * canvasW / totalLength
  const xStart = 0.05 * totalLength * xScale

  let x1 = 0
  let x2 = 0
  for( var i = 0; i < beam.nodes.length-1; i++ ) {
    x1 = xStart + xScale * beam.nodes[i].x;
    x2 = xStart + xScale * beam.nodes[i+1].x;
    drawBeamSpan(x1, x2 - x1, project, paper)
  }

  // scale gradiant values to fit the canvas. the maximum value should be plus or minus 5 times the height of the beam
  // (depending on the minimum and maximum values)
  // this is a bit arbitrary but it should give a good visual representation of the beam diagram
  const maxGradientValue = Math.max(...gradientValues.map(value => Math.abs(value)));
  const normalizedGradientValues = gradientValues.map(value => {
    return (value / maxGradientValue) * (BEAM_T * 3);
  });


  drawDiagram(xs, normalizedGradientValues, Y_OFFSET, paper)
}

function drawBeamSpan(x, spanLength, project, paper) {

  let span_attr = Object.assign({}, steel_span_attr);

  if(project.isCustom || project.isAluminum) {
      span_attr = Object.assign({}, custom_shape_span_attr); // clone the object to avoid modifying the original
  } else if(project.isNorthAmericanSteel || project.isEuropeanSteel || project.isASTMSteel) {
      span_attr = Object.assign({}, steel_span_attr); // clone the object to avoid modifying the original
  } else if(project.isWood || project.isEngineeredLumber) {
      span_attr = Object.assign({}, wood_span_attr); // clone the object to avoid modifying the original
  }

  span_attr['fill-opacity'] = 0.5; // set the opacity to 50%
  
  var b = paper.rect(x, Y_OFFSET - BEAM_T  / 2, spanLength, BEAM_T);
  b.attr(span_attr);

  var top_path_offset = 0;
  var bottom_path_offset = 0;
  if (hasFlanges(project.shape)) {
    top_path_offset = 4;
    bottom_path_offset = 4;
  }
  if (hasTopFlange(project.shape)) {
    top_path_offset = 4;
    bottom_path_offset = 0;
  }
  if (hasBottomFlange(project.shape)) {
    top_path_offset = 0;
    bottom_path_offset = 4;
  }

  const y = Y_OFFSET - BEAM_T / 2;
  const x1 = x;
  const x2 = x + spanLength;
  var pathStr = pathM(x1, y  + top_path_offset) + pathL(x2, y + top_path_offset)
  pathStr += pathM(x2, y + BEAM_T - bottom_path_offset) + pathL(x1, y + BEAM_T - bottom_path_offset);
  let p = paper.path(pathStr).attr(span_attr);

  p.attr(span_attr);
}


function drawDiagram(xs, values, Y_OFFSET, paper) {
  let pathStr = pathM( xs[xs.length - 1], Y_OFFSET )
  pathStr += pathL(xs[0], Y_OFFSET )

  for (let i = 0; i < xs.length; i++) {
    const y = 10
    if(i !== 0) {
      pathStr += pathM(xs[i], Y_OFFSET)
    }
    pathStr += pathL(xs[i], Y_OFFSET - values[i])
    if (i < xs.length - 1) {
      pathStr += pathL(xs[i + 1], Y_OFFSET - values[i + 1]);
    }
  }

  paper.path(pathStr).attr({
    'stroke': '#000',
    'stroke-width': 2,
    'fill': 'none'
  });

}

function hasFlanges(shapeData) {
  var type = shapeData['Type'];
  return (type === "W" || type === "M" || type === "S" || type === "HP");
}

function hasTopFlange(shapeData) {
  var type = shapeData['Type'];
  return (type === "WT" || type === "MT" || type === "ST");
}

function hasBottomFlange(shapeData) {
  var type = shapeData['Type'];
  return (type === "L");
}

function pathM(x, y) { return " M " + x + " " + y + " "; }

function pathL(x, y) { return " L " + x + " " + y + " "; }
